import Heading from '../../../components/Heading'
import Text from '../../../components/Text'
import React, { useEffect } from 'react'
import { useAdmin } from '../../../contexts/AdminContext'
import IconButton from '../../../components/IconButton'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import Card from '../../../components/Card'
import Button from '../../../components/Button'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import styles from './SubscriptionChange.module.css'
import ConfirmModal from '../../../components/ConfirmModal'
import { closeModal, openModal } from '../../../services/ModalService'
import { showToast } from '../../../services/ToastService'
import { useNavigate } from 'react-router-dom'
import { usePluginContext } from '../../../contexts/PluginContext'

const SubscriptionChange: React.FC = () => {
    const {
        plans,
        activeSub,
        getPlans,
        service,
        getSubscriptions,
        getSubscription,
    } = useAdmin()
    const { t } = useTranslation(['settings'])
    const { plugin } = usePluginContext()
    const navigate = useNavigate()

    useEffect(() => {
        getPlans()
    }, [getPlans])

    const addPlan = (plan_id: string) => {
        if (plugin)
            service
                .addPlanToSub(plugin.plugin, plan_id)
                .then(() => {
                    showToast(t('subscription.planSuccessfullyAdded'))
                    getSubscription()
                    getSubscriptions()
                    navigate('/subscription')
                })
                .catch(() => {
                    showToast(t('subscription.errorBookingThePlan'), 'error')
                })
    }

    const removePlan = (plan_id: string) => {
        if (plugin)
            service
                .removePlanFromSub(plugin.plugin, plan_id)
                .then(() => {
                    showToast(t('subscription.planSuccessfullyTerminated'))
                    getSubscription()
                    getSubscriptions()
                    closeModal()
                    navigate('/subscription')
                })
                .catch(() => {
                    showToast(t('subscription.errorPlanTerminated'), 'error')
                })
    }

    const showRemoveModal = (plan_id: string) => {
        openModal({
            heading: t('common.confirmation'),
            content: (
                <ConfirmModal
                    text={t('subscription.removePlanQuestion')}
                    onSubmit={() => removePlan(plan_id)}
                    onAbort={() => closeModal()}
                />
            ),
        })
    }

    const showStopModal = () => {
        openModal({
            heading: t('common.confirmation'),
            content: (
                <ConfirmModal
                    text={t('subscription.stopSubQuestion')}
                    onSubmit={() => stopSub()}
                    onAbort={() => closeModal()}
                />
            ),
        })
    }

    const stopSub = () => {
        if (plugin)
            service
                .stopSub(plugin.plugin)
                .then(() => {
                    showToast(
                        'top-right',
                        'success',
                        t('subscription.subscriptionSuccessfullyTerminated')
                    )
                    getSubscription()
                    getSubscriptions()
                    closeModal()
                    navigate('/subscription')
                })
                .catch(() => {
                    showToast(
                        'top-right',
                        'error',
                        t('subscription.errorTerminatingTheSubscription')
                    )
                })
    }

    const resumeSub = () => {
        if (plugin)
            service
                .resumeSub(plugin.plugin)
                .then(() => {
                    showToast(
                        'top-right',
                        'success',
                        t('subscription.subscriptionSuccessfullyReactivated')
                    )
                    getSubscription()
                    getSubscriptions()
                    navigate('/subscription')
                })
                .catch(() => {
                    showToast(
                        'top-right',
                        'error',
                        t('subscription.errorReactivatingTheSubscription')
                    )
                })
    }

    const changeHost = () => {
        if (plugin && activeSub)
            service
                .changeHost(plugin.plugin, {
                    host: window.location.host,
                    key: activeSub.stripe_id,
                })
                .then(() => {
                    showToast(
                        'top-right',
                        'success',
                        t('subscription.subscriptionChangedHostSuccessful')
                    )
                    getSubscription()
                    getSubscriptions()
                    navigate('/subscription')
                })
                .catch(() => {
                    showToast(
                        'top-right',
                        'error',
                        t('subscription.subscriptionChangedHostError')
                    )
                })
    }

    return (
        <div>
            <Heading level={3} classes={'zr-mb-4'}>
                {t('subscription.makeChangesToTheSubscription')}
            </Heading>
            <IconButton
                icon={faArrowLeft}
                color={'black'}
                onClick={() => navigate('/subscription')}
            >
                {t('subscription.back')}
            </IconButton>
            <Card classes={'zr-my-3'}>
                <Heading level={4}>
                    {t('subscription.connectSubscription')}
                </Heading>
                <Text classes={'zr-my-3'}>
                    {t('subscription.makeChangesToTheSubsText')}
                </Text>
                <Button
                    classes={'zr-mt-3'}
                    hierarchy={'primary'}
                    text={t('subscription.connectSubscription')}
                    fn={() => changeHost()}
                />
            </Card>
            <Heading level={3} classes={'zr-mt-6 zr-mb-4'}>
                {t('subscription.editPlans')}
            </Heading>
            <div className={cx(styles['plans'])}>
                {plans
                    .filter((p) => !!p.dependent_id)
                    .map((p) => {
                        return (
                            <Card key={p.id}>
                                <Heading level={4}>
                                    {p.name} (
                                    {p.price.toString().replace('.', ',')} €)
                                </Heading>
                                <div className={cx(styles['planDescription'])}>
                                    <Text>{p.description}</Text>
                                </div>
                                <div className={cx(styles['planButtons'])}>
                                    {activeSub?.plans
                                        .map((a) => a.id)
                                        .includes(p.id) ? (
                                        <Button
                                            hierarchy={'secondary'}
                                            text={t('subscription.endPlan')}
                                            fn={() =>
                                                showRemoveModal(p.stripe_plan)
                                            }
                                        />
                                    ) : (
                                        <Button
                                            hierarchy={'primary'}
                                            text={t('subscription.addPlan')}
                                            fn={() => addPlan(p.stripe_plan)}
                                        />
                                    )}
                                </div>
                            </Card>
                        )
                    })}
            </div>
            {!activeSub?.end_date ? (
                <>
                    <Heading level={3} classes={'zr-mt-6 zr-mb-4'}>
                        {t('subscription.cancelSubscription')}
                    </Heading>
                    <Card classes={'zr-mb-4'}>
                        <Text classes={'zr-my-3'}>
                            {t('subscription.cancelSubsText')}
                        </Text>
                        <Button
                            hierarchy={'secondary'}
                            text={t('subscription.cancelSubscription')}
                            fn={() => showStopModal()}
                        />
                    </Card>
                </>
            ) : (
                <>
                    <Heading level={3} classes={'zr-mt-6 zr-mb-4'}>
                        {t('subscription.reactivateSubscription')}
                    </Heading>
                    <Card classes={'zr-mb-4'}>
                        <Text classes={'zr-my-3'}>
                            {t('subscription.reactivateSubscriptionText')}
                        </Text>
                        <Button
                            hierarchy={'secondary'}
                            text={t('subscription.reactivateSubscription')}
                            fn={() => resumeSub()}
                        />
                    </Card>
                </>
            )}
        </div>
    )
}

export default SubscriptionChange
